<script setup lang="ts">
/**
 * "แชทเก่าจาก Facebook" in the customer panel (api/app/fb_history.py): the customer's older Messenger / Instagram
 * conversation, pulled once when they first wrote to us. Summary + "เปิดดูแชทเก่า" (a read-only history chat),
 * why nothing is there, and for owner/head "ดึงประวัติจาก Facebook อีกครั้ง" (same switch as the automatic pull).
 * Asks again every few seconds while the pull is still running.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { fbHistoryApi, prepareHistoryChat, type FbHistoryStatus } from '../../api/fbhistory'
import type { ConversationDetail } from '../../api/types'
import { doneNote, isBusy, platformName, rangeText, refusedLine, statusLine } from '../../lib/fbHistory'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const store = useInbox()
const clock = useClock()
const router = useRouter()

const status = ref<FbHistoryStatus | null>(null)
const error = ref<string | null>(null)
const pulling = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null
let seq = 0

const contactId = computed(() => props.detail.contact.id)

async function load(): Promise<void> {
  const id = contactId.value
  if (!id) return
  const mine = ++seq
  try {
    const s = await fbHistoryApi().status(id)
    if (mine !== seq) return
    status.value = s
    error.value = null
  } catch (e) {
    if (mine !== seq) return
    error.value = e instanceof Error ? e.message : String(e)
  }
  schedule()
}

function schedule(): void {
  if (timer) clearTimeout(timer)
  timer = isBusy(status.value) ? setTimeout(() => void load(), 5000) : null
}

watch(
  contactId,
  () => {
    status.value = null
    error.value = null
    void load()
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  seq++
  if (timer) clearTimeout(timer)
})

const s = computed(() => status.value)
const line = computed(() => (s.value?.supported ? statusLine(s.value, clock.now) : null))
const refused = computed(() => (s.value ? refusedLine(s.value) : null))
const note = computed(() => (s.value ? doneNote(s.value) : null))
const name = computed(() => (s.value?.supported ? platformName(s.value) : 'Facebook'))
const busy = computed(() => isBusy(s.value))

function openHistory(): void {
  const h = s.value?.history
  if (!h) return
  prepareHistoryChat(props.detail.id)
  void router.push({ name: 'conversation', params: { id: h.conversation_id } })
}

async function pull(): Promise<void> {
  const id = contactId.value
  if (!id || pulling.value) return
  pulling.value = true
  try {
    await fbHistoryApi().pull(id)
    store.flash(`เริ่มดึงประวัติจาก ${name.value} แล้ว · ใช้เวลาสักครู่`)
    await load()
  } catch (e) {
    store.flash(e instanceof Error ? e.message : String(e))
  } finally {
    pulling.value = false
  }
}
</script>

<template>
  <section v-if="s?.supported || error" class="cs fbh" :aria-label="`แชทเก่าจาก ${name}`">
    <div class="sec-h">
      <Icon name="history" class="fbh-ic" />
      <h3 class="sec-t">แชทเก่าจาก {{ name }}</h3>
      <span class="grow"></span>
      <span v-if="busy" class="fbh-live" aria-live="polite"><span class="dot"></span>กำลังดึง</span>
    </div>

    <p v-if="error" class="fbh-l t-warn"><Icon name="alertCircle" /><span>โหลดสถานะไม่สำเร็จ: {{ error }}</span></p>

    <template v-else-if="s">
      <div v-if="s.history" class="fbh-sum">
        <div class="fbh-sum-b">
          <span class="fbh-n">{{ rangeText(s.history) }}</span>
          <span v-if="note" class="fbh-note">{{ note }}</span>
        </div>
        <button type="button" class="fbh-open" :aria-label="`เปิดดูแชทเก่าจาก ${name}`" @click="openHistory">
          <span>เปิดดูแชทเก่า</span><Icon name="arrowRight" />
        </button>
      </div>

      <p v-if="refused" class="fbh-l t-warn"><Icon name="alert" /><span>{{ refused }}</span></p>
      <p v-if="line" class="fbh-l" :class="`t-${line.tone}`">
        <Icon :name="line.tone === 'warn' ? 'alertCircle' : line.tone === 'wait' ? 'clock' : 'info'" /><span>{{ line.text }}</span>
      </p>

      <button v-if="s.can_pull" type="button" class="fbh-pull" :disabled="pulling" @click="pull">
        <Icon name="refresh" />{{ pulling ? 'กำลังสั่งดึง…' : s.job ? `ดึงประวัติจาก ${name} อีกครั้ง` : `ดึงประวัติจาก ${name}` }}
      </button>
    </template>
  </section>
</template>

<style scoped>
.fbh {
  gap: 10px;
}
.fbh-ic {
  width: 16px;
  height: 16px;
  color: var(--mut);
}
.grow {
  flex: 1;
}
.fbh-live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--ac2);
  font-weight: 600;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  animation: fbh-pulse 1.4s ease-in-out infinite;
}
@keyframes fbh-pulse {
  50% {
    opacity: 0.35;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: none;
  }
}
.fbh-sum {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fbfcfb;
}
.fbh-sum-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.fbh-n {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
  color: var(--ink);
}
.fbh-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.fbh-open {
  flex: none;
  height: 34px;
  padding: 0 10px 0 12px;
  border-radius: 9px;
  border: 1px solid #bfdccd;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
  white-space: nowrap;
}
.fbh-open:hover {
  background: var(--acs);
}
.fbh-open :deep(.ic) {
  width: 15px;
  height: 15px;
}
.fbh-l {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.fbh-l :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  margin-top: 2px;
}
.fbh-l.t-warn {
  color: #7a4700;
}
.fbh-l.t-warn :deep(.ic) {
  color: #b86e00;
}
.fbh-l.t-wait {
  color: var(--ac2);
}
.fbh-pull {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 8px 0 6px;
  margin-left: -6px;
  border-radius: 7px;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: var(--ac2);
  background: none;
}
.fbh-pull:hover:not(:disabled) {
  background: #eef2f0;
}
.fbh-pull:disabled {
  color: #8a9a92;
}
.fbh-pull :deep(.ic) {
  width: 14px;
  height: 14px;
}
</style>
